import { useRef, type PointerEvent as ReactPointerEvent } from "react";
import type { CSSProperties } from "react";

import type { MatchProfile } from "./types";
import { affinityScore, AFFINITY_AXES, type AffinityAxis } from "./affinity";
import { tierOf } from "./chromePlan";
import { scoreBand, scoreInk } from "./weights";
import { useT } from "../i18n";

const MOODS = ["ember", "ink", "olive", "dusk"] as const;
export type CardMood = (typeof MOODS)[number];

/** Press duration before the stats back peeks. */
const HOLD_MS = 240;

const AXIS_STAT_KEY: Record<AffinityAxis, string> = {
  faith: "match.cardStatFaith",
  character: "match.cardStatCharacter",
  purpose: "match.cardStatPurpose",
  home: "match.cardStatHome",
  ancestry: "match.cardStatAncestry",
  temperament: "match.cardStatTemper",
};

export function cardMoodOf(id: string): CardMood {
  let n = 0;
  for (let i = 0; i < id.length; i += 1) n += id.charCodeAt(i);
  return MOODS[n % MOODS.length];
}

type StatRow = { key: string; labelKey: string; value: number };

/** Pair matching axes when both answered; else their scales as 0–100 (×10). */
function backStats(self: MatchProfile | null, profile: MatchProfile): StatRow[] {
  if (self?.answers && profile.answers) {
    const aff = affinityScore(
      self.answers,
      profile.answers,
      self.scales,
      profile.scales
    );
    const rows = AFFINITY_AXES
      .map((axis) => ({
        key: axis,
        labelKey: AXIS_STAT_KEY[axis],
        value: Math.round(aff.axes[axis]),
      }))
      .filter((r) => r.value > 0);
    if (rows.length) return rows.slice(0, 6);
  }
  const s = profile.scales;
  return [
    { key: "loyalty", labelKey: "match.cardStatLoyalty", value: Math.round(s.loyalty * 10) },
    { key: "honesty", labelKey: "match.cardStatHonesty", value: Math.round(s.honesty * 10) },
    { key: "passion", labelKey: "match.cardStatPassion", value: Math.round(s.passion * 10) },
    { key: "commitment", labelKey: "match.cardStatCommit", value: Math.round(s.commitment * 10) },
  ];
}

export function WeekFifaCard({
  profile,
  self,
  score,
  flipped,
  revealed,
  mutual,
  disabled,
  onPeek,
  onGoToProfile,
}: {
  profile: MatchProfile;
  self: MatchProfile | null;
  score: number;
  flipped: boolean;
  /** True after you sent a like — unlocks full profile. */
  revealed: boolean;
  /** True after they liked you back — unlocks chat. */
  mutual: boolean;
  disabled?: boolean;
  onPeek: (open: boolean) => void;
  onGoToProfile: () => void;
}) {
  const t = useT();
  const mood = cardMoodOf(profile.id);
  const band = scoreBand(score);
  const stats = backStats(self, profile);
  const about = profile.aboutYou?.trim() || "";
  const looking = profile.lookingFor?.trim() || "";
  const holdTimer = useRef<number | null>(null);
  const holding = useRef(false);
  const didHold = useRef(false);

  const clearHold = () => {
    if (holdTimer.current != null) {
      window.clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
  };

  const endPeek = () => {
    clearHold();
    if (holding.current) {
      holding.current = false;
      onPeek(false);
    }
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    if (disabled) return;
    if (e.button !== 0) return;
    didHold.current = false;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    clearHold();
    holdTimer.current = window.setTimeout(() => {
      holding.current = true;
      didHold.current = true;
      onPeek(true);
    }, HOLD_MS);
  };

  const onPointerUp = (e: ReactPointerEvent<HTMLElement>) => {
    if (disabled) return;
    const wasHold = didHold.current;
    endPeek();
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (!wasHold && revealed) {
      onGoToProfile();
    }
  };

  return (
    <article
      className={`match-week-tile match-week-tile--fifa${flipped ? " is-flipped" : ""}${
        revealed ? " is-revealed" : ""
      }${mutual ? " is-mutual" : ""}`}
      data-plan={tierOf(profile)}
      data-mood={mood}
      data-score-band={band ?? undefined}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={endPeek}
      onContextMenu={(e) => e.preventDefault()}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={
        revealed
          ? t("match.holdOrOpenProfile", { name: profile.firstName })
          : t("match.holdToPeek", { name: profile.firstName })
      }
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          if (revealed) onGoToProfile();
          else onPeek(true);
        }
        if (e.key === "Escape") onPeek(false);
      }}
      onBlur={() => onPeek(false)}
    >
      <div
        className="match-week-tile__face match-week-tile__face--front"
        aria-hidden={flipped}
      >
        <span className="match-week-tile__foil" aria-hidden />
        <span
          className="match-week-tile__rating"
          aria-hidden
          style={
            {
              "--score-a": scoreInk(score).a,
              "--score-b": scoreInk(score).b,
            } as CSSProperties
          }
        >
          <strong>{score}</strong>
          <em>/100</em>
        </span>
        <span className="match-week-tile__photo">
          <span className="match-week-tile__monogram">{profile.firstName.charAt(0)}</span>
          {profile.photoUrl ? (
            <img
              className="match-week-tile__img"
              src={profile.photoUrl}
              alt=""
              decoding="async"
              loading="lazy"
              draggable={false}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : null}
        </span>
      </div>
      <div
        className="match-week-tile__face match-week-tile__face--back"
        aria-hidden={!flipped}
      >
        <span className="match-week-tile__foil" aria-hidden />
        <div className="match-week-tile__back-panel">
          <div className="match-week-tile__back-head">
            <span className="match-week-tile__back-score" aria-label={`${score} / 100`}>
              <span className="match-week-tile__back-score-n">{score}</span>
              <span className="match-week-tile__back-score-den">/100</span>
            </span>
            <span className="match-week-tile__back-name">
              {t("match.personLine", { name: profile.firstName, age: profile.age })}
            </span>
            <p className="match-week-tile__back-meta">
              {profile.city}
              {profile.profession ? ` · ${profile.profession}` : ""}
            </p>
            <p className="match-week-tile__back-score-label">{t("match.matchingScore")}</p>
          </div>
          <ul className="match-week-tile__fifa-stats">
            {stats.map((row) => (
              <li key={row.key} className="match-week-tile__fifa-stat">
                <span className="match-week-tile__fifa-stat-lab">{t(row.labelKey)}</span>
                <span className="match-week-tile__fifa-stat-val">{row.value}</span>
              </li>
            ))}
          </ul>
          {about ? (
            <p className="match-week-tile__back-block">{about}</p>
          ) : null}
          {looking ? (
            <p className="match-week-tile__back-block">
              <span className="match-week-tile__back-kicker">{t("match.cardSeeking")}</span>
              {looking}
            </p>
          ) : null}
          {revealed ? (
            <button
              type="button"
              className="match-week-tile__go-profile"
              disabled={disabled}
              onPointerDown={(e) => {
                e.stopPropagation();
                clearHold();
              }}
              onClick={(e) => {
                e.stopPropagation();
                endPeek();
                onGoToProfile();
              }}
            >
              {mutual ? t("match.goToProfileChat") : t("match.goToProfile")}
            </button>
          ) : (
            <p className="match-week-tile__hold-hint">{t("match.likeToOpenProfile")}</p>
          )}
        </div>
      </div>
    </article>
  );
}
